/**
 * NotificationPopper - to display top 5 notification to user
 *
 * @version 1.0.1
 * @author [Karthick Shanmugam](https://github.com/karthickShanmugam0689)
 */
import * as React from 'react'
import {useQuery, useMutation} from '@apollo/client'
import {
  getNotifications,
  readAllNotificationsMutation,
} from '../../queries/notificationQuery'
import SXTNotificationList from '../common/SXTNotification/SXTNotificationList'
import {NavLink} from 'react-router-dom'
import {INotification} from '../common/SXTNotification/SXTNotification.d'
import Loader from '../common/Loader/Loader'
import DraftsIcon from '@mui/icons-material/Drafts'
import IconButton from '@mui/material/IconButton'
import {fetchNotifications} from './utils.Notifications'
import {logError} from '../../utils/genericUtils'
import './NotificationPopper.scss'

const NOTIFICATIONS_ALLOWED_IN_POPPER = 5

const getRequiredNotifications = (
  notificationList: INotification[] | null | undefined
) => {
  if (!notificationList?.length) return []
  return notificationList.slice(0, NOTIFICATIONS_ALLOWED_IN_POPPER)
}

const NotificationPopper = ({
  handleToggle,
}: {
  handleToggle: (toggle: boolean) => void
}) => {
  const [scrollEl, setScrollEl] = React.useState<HTMLDivElement | null>(null)

  const [readAllNotifications] = useMutation(readAllNotificationsMutation)
  const {
    data,
    error,
    loading: isLoading,
  } = useQuery(getNotifications, {
    variables: {
      since: -1,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })

  const rawNotifications = data?.notifications ?? []

  const [notificationsToRead, setNotificationsToRead] = React.useState<
    INotification[]
  >([])
  const [isMerging, setIsMerging] = React.useState(false)

  const sortedNotifications = React.useMemo(() => {
    if (!rawNotifications?.length) return []
    try {
      return fetchNotifications(rawNotifications)
    } catch (err) {
      logError('[NotificationPopper] Error processing notifications:', err)
      return []
    }
  }, [rawNotifications])

  React.useEffect(() => {
    let mounted = true
    const updateNotifications = async () => {
      if (!mounted) return
      setIsMerging(true)
      try {
        const finalNotifications = getRequiredNotifications(sortedNotifications)
        setNotificationsToRead(finalNotifications)
      } catch (err) {
        logError('[NotificationPopper] Error updating notifications:', err)
      } finally {
        setIsMerging(false)
      }
    }

    updateNotifications()
    return () => {
      mounted = false
    }
  }, [sortedNotifications])

  const markAllNotificationsAsRead = async () => {
    try {
      await readAllNotifications()
      handleToggle(false)
    } catch (err) {
      logError('[NotificationPopper] Error marking notifications as read:', err)
    }
  }

  if (error) {
    return <div className="error-block">Error! {error?.message}</div>
  }

  return (
    <div className="notification-popper" ref={setScrollEl}>
      {isLoading || isMerging ? (
        <Loader isInContainer={true} />
      ) : (
        <>
          {notificationsToRead.length > 0 && (
            <IconButton
              className="mark-read"
              onClick={markAllNotificationsAsRead}
              size="large"
            >
              <DraftsIcon color="inherit" fontSize="small" />
              Mark all as read
            </IconButton>
          )}
          <SXTNotificationList
            notifications={notificationsToRead}
            isLighterView={true}
            root={scrollEl}
          />
          {notificationsToRead.length > 4 && (
            <NavLink
              to="/notification"
              className="view-all"
              onClick={() => handleToggle(false)}
            >
              View All
            </NavLink>
          )}
        </>
      )}
    </div>
  )
}

export default React.memo(NotificationPopper)
